<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-y-auto relative">
    <!-- Table Header with Summary -->
    <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
      <div class="space-y-4">
        <!-- Header -->
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

            <h3 class="text-lg font-semibold text-gray-900">
              Packages
            </h3>
          </div>

          <div class="flex items-center space-x-3">
            <button
              type="button"
              class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              data-test="previous-month"
              @click="navigateMonth(-1)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
  <path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clip-rule="evenodd" />
</svg>
            </button>

            <div class="flex items-center space-x-2">
              <AppInput
                v-model="selectedMonth"
                type="month"
                name="date"
                data-test="date"
                class="form-input w-auto"
              />
            </div>

            <button
              type="button"
              class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              data-test="next-month"
              @click="navigateMonth(1)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
            </button>
          </div>
        </div>

        <!-- Summary Row -->
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex justify-between gap-4">
            <div class="text-center md:text-left">
              <p class="text-sm text-gray-500">
                Subcontractors
              </p>

              <p class="text-lg font-semibold text-gray-900">
                {{ subcontractors.length }}
              </p>
            </div>

            <div class="text-center md:text-right ml-auto">
              <p class="text-sm text-gray-500">
                Claims for month
              </p>

              <div class="flex items-center justify-end space-x-2">
                <div
                  v-if="subcontractorSummary.draft > 0"
                  class="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full"
                >
                  {{ subcontractorSummary.draft }} Draft
                </div>

                <div
                  v-if="subcontractorSummary.draft_subcontractor > 0"
                  class="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full"
                >
                  {{ subcontractorSummary.draft_subcontractor }} Issued
                </div>

                <div
                  v-if="subcontractorSummary.submitted > 0"
                  class="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full"
                >
                  {{ subcontractorSummary.submitted }} Submitted
                </div>

                <div
                  v-if="subcontractorSummary.certified > 0"
                  class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                >
                  {{ subcontractorSummary.certified }} Certified
                </div>

                <span>
                  {{ packagesWithClaim }}/{{ packageRows.length }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Packages List -->
    <div
      class="overflow-x-auto relative"
      :class="{ 'opacity-50': fetchDashboardAction.is(States.LOADING) }"
    >
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr class="child:px-2">
            <th class="py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Package
            </th>

            <th class="py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Scope Gap
            </th>

            <th class="py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Approved Variation
            </th>

            <th class="py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Final Account
            </th>

            <th class="py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Final Forecast
            </th>

            <th class="py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Claim for Month
            </th>

            <th class="py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Period
            </th>

            <th class="py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Time Bar
            </th>

            <th class="py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Amount
            </th>

            <th class="py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Cumulative Amount
            </th>

            <th class="py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" />
          </tr>
        </thead>

        <tbody class="bg-white divide-y divide-gray-100">
          <PackageRow
            v-for="row in packageRows"
            :key="row.package.id"
            :selected-month="selectedMonthAsDate"
            :subcontractor-package="row.package"
            :project-supplier="row.projectSupplier"
            :lite-mode="!!project?.feature_flag_subcontractor_claims_lite"
            show-package-details
            class="hover:bg-gray-50 transition-colors duration-150 ease-in-out"
            @create-claim="onCreateClaim"
            @issue-claim="emit('issue-claim', $event)"
          />
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import useDayQueryParam from '@/composables/useDayQueryParam';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { getNextTimeBarSortKey } from '@/helpers';
import Claim, { ClaimStatus } from '@/models/Claim';
import Package from '@/models/Package';
import ProjectSupplier from '@/models/ProjectSupplier';
import { usePackagesStore } from '@/store/packages';
import { useProjectsStore } from '@/store/projects';
import { useSuppliersStore } from '@/store/supplier';
import { DateTime } from 'luxon';
import { computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import PackageRow from './PackageRow.vue';

const props = defineProps<{
  projectId: string;
}>();

const emit = defineEmits<{
  (e: 'create-claim', data: { projectSupplier: ProjectSupplier; packageData: Package; latestClaim?: Claim }): void;
  (e: 'issue-claim', data: { claim: Claim; email: string }): void;
}>();

const selectedMonth = defineModel<string>('selectedMonth', { required: true });
const packagesStore = usePackagesStore();
const suppliersStore = useSuppliersStore();
const projectsStore = useProjectsStore();
const project = computed(() => projectsStore.project);
const fetchDashboardAction = useStoreApiAction(packagesStore.fetchSubcontractorDashboard);
const selectedDate = useDayQueryParam();
const router = useRouter();

if(!selectedMonth.value) {
  selectedMonth.value = DateTime.fromJSDate(selectedDate.value).toFormat('yyyy-MM');
}

watch(selectedMonth, (newValue) => {
  router.replace({ query: { day: newValue } });
});

const selectedMonthAsDate = computed(() => {
  return DateTime.fromFormat(selectedMonth.value, 'yyyy-MM');
});

watch(selectedMonth, (month, previousMonth) => {
  if(!previousMonth) {
    return;
  }

  if(project.value?.id) {
    fetchDashboardAction.request(project.value.id, { month });
  }
});

const packages = computed(() => {
  return packagesStore.models
    .where('project_id', Number(props.projectId))
    .with('subcontractor')
    .with('package_claims', (query) => {
      query.whereHas('claim', (query) => {
        query.where('start', (start) => {
          return start >= selectedMonthAsDate.value.startOf('month').toISO() &&
            start <= selectedMonthAsDate.value.endOf('month').toISO();
        });
      })
        .with('claim')
        .orderBy('claim_number', 'desc');
    })
    .get();
});

const subcontractors = computed(() => {
  return suppliersStore.models
    .whereHas('packages', (query) => {
      query.where('project_id', Number(props.projectId));
    })
    .with('projects', (query) => {
      query.where('project_id', Number(props.projectId))
        .with('supplier');
    })
    .with('packages', (query) => {
      query.where('project_id', Number(props.projectId))
        .with('package_claims', (query) => {
          query.with('claim');
        });
    })
    .get();
});

const projectSupplierBySubcontractorId = computed(() => {
  const map = new Map<number, ProjectSupplier>();

  subcontractors.value.forEach((subcontractor) => {
    const projectSupplier = subcontractor.projects?.[0];

    if(projectSupplier) {
      map.set(subcontractor.id, projectSupplier);
    }
  });

  return map;
});

const getLatestClaimForPackage = (subcontractorPackage: Package) => {
  return subcontractorPackage.package_claims?.[0]?.claim || undefined;
};

const packageRows = computed(() => {
  return packages.value
    .map((pkg) => ({
      package: pkg,
      projectSupplier: projectSupplierBySubcontractorId.value.get(pkg.subcontractor_id),
    }))
    .filter((row) => !!row.projectSupplier)
    .sort((a, b) => {
      const aSortKey = getNextTimeBarSortKey(
        getLatestClaimForPackage(a.package),
        project.value,
        selectedMonthAsDate.value,
      );

      const bSortKey = getNextTimeBarSortKey(
        getLatestClaimForPackage(b.package),
        project.value,
        selectedMonthAsDate.value,
      );

      if(aSortKey !== bSortKey) {
        return aSortKey - bSortKey;
      }

      return a.package.name.localeCompare(b.package.name);
    });
});

const packagesWithClaim = computed(() => {
  return packages.value.filter((pkg) => {
    return getLatestClaimForPackage(pkg) !== undefined;
  }).length;
});

const subcontractorSummary = computed(() => {
  const summary = { draft: 0, draft_subcontractor: 0, submitted: 0, certified: 0, noClaim: 0, total: 0 };

  packageRows.value.forEach((pkg) => {
    const latestClaim = getLatestClaimForPackage(pkg.package);

    if(!latestClaim) {
      summary.noClaim++;
    } else if(latestClaim.status === ClaimStatus.draft) {
      summary.draft++;
      summary.total++;
    } else if(latestClaim.status === ClaimStatus.draft_subcontractor) {
      summary.draft_subcontractor++;
      summary.total++;
    } else if(latestClaim.status === ClaimStatus.subcontractor_claim) {
      summary.submitted++;
      summary.total++;
    } else if(latestClaim.status === ClaimStatus.certified) {
      summary.certified++;
      summary.total++;
    }
  });

  return summary;
});

const navigateMonth = (direction: number) => {
  const currentMonth = DateTime.fromFormat(selectedMonth.value, 'yyyy-MM');
  const newMonth = currentMonth.plus({ months: direction });

  selectedMonth.value = newMonth.toFormat('yyyy-MM');
};

const onCreateClaim = (data: { projectSupplier: ProjectSupplier; packageData: Package; latestClaim?: Claim }) => {
  emit('create-claim', data);
};
</script>

<style scoped></style>
